import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ALL_DIAL_CODES } from '../../constants/phoneDialCodes';
import { getPhoneLengthRule } from '../../applicant/utils/contactFieldValidation';
import { showPopupMessage } from '../../utils/showPopup';

const COUNTRIES = (() => {
  const seen = new Set();
  const out = [];
  for (const d of ALL_DIAL_CODES) {
    const code = String(d?.code || '').trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push({
      code,
      name: String(d?.name || '').trim(),
      flag: String(d?.flag || '').trim() || '🌐',
    });
  }
  return out;
})();

function flagToIso2(flag) {
  const parts = [...String(flag || '')];
  if (parts.length < 2) return '';
  const base = 0x1f1e6;
  const a = parts[0].codePointAt(0);
  const b = parts[1].codePointAt(0);
  if (!a || !b) return '';
  if (a < base || a > base + 25 || b < base || b > base + 25) return '';
  return String.fromCharCode((a - base) + 65, (b - base) + 65).toLowerCase();
}

function getCountryIso2(item) {
  const fromFlag = flagToIso2(item?.flag);
  if (fromFlag) return fromFlag;
  if (item?.code === '+1') return 'us';
  return 'un';
}

function digitsOnly(value = '') {
  return String(value || '').replace(/\D/g, '');
}

function splitInternationalPhone(rawValue, fallbackCountryCode) {
  const raw = String(rawValue || '').trim();
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, '');
  const withPlus = normalized.startsWith('+')
    ? normalized
    : normalized.startsWith('00')
      ? `+${normalized.slice(2)}`
      : '';
  if (!withPlus) return null;

  const sorted = [...ALL_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const { code } of sorted) {
    if (withPlus.startsWith(code)) {
      return {
        dial: code,
        national: digitsOnly(withPlus.slice(code.length)),
      };
    }
  }

  return {
    dial: fallbackCountryCode || '+34',
    national: digitsOnly(withPlus),
  };
}

export function extractLocalNumber(value = '') {
  const sorted = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
  for (const c of sorted) {
    if (value.startsWith(c.code + ' ')) return value.slice(c.code.length + 1);
    if (value.startsWith(c.code) && value.length > c.code.length) return value.slice(c.code.length);
  }
  return value;
}

export default function MobileFieldWithVerify({
  value,
  onChange,
  onAfterChange,
  countryCode = '+34',
  onCountryCodeChange,
  channel,
  onChannelChange,
  verified,
  onVerifiedChange,
  onRequestOtp,
  onVerifyOtp,
  onError,
  lockInputsWhileVerifying = false,
  onVerifyingChange,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');
  const [lastVerifiedValue, setLastVerifiedValue] = useState(null);
  const [otpMessage, setOtpMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  useEffect(() => {
    if (verified && lastVerifiedValue != null && value !== lastVerifiedValue) {
      onVerifiedChange(false);
      setLastVerifiedValue(null);
      setVerifying(false);
      setCode('');
    }
  }, [value, verified, lastVerifiedValue, onVerifiedChange]);

  useEffect(() => {
    onVerifyingChange?.(verifying && !verified);
  }, [verifying, verified, onVerifyingChange]);

  useEffect(() => {
    // SMS-only OTP flow for now.
    if (onChannelChange && channel !== 'SMS') {
      onChannelChange('SMS');
    }
  }, [channel, onChannelChange]);

  const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];

  const filteredCountries = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.code.toLowerCase().includes(term) || c.name.toLowerCase().includes(term)
    );
  }, [searchTerm]);
  const { min: minNationalDigits, max: maxNationalDigits } = getPhoneLengthRule(countryCode || '+34');
  const nationalValue = digitsOnly(extractLocalNumber(value || ''));
  const phoneValid = nationalValue.length >= minNationalDigits && nationalValue.length <= maxNationalDigits;
  const inputsLocked = lockInputsWhileVerifying && verifying && !verified;

  const applyRawPhoneValue = (rawValue) => {
    const split = splitInternationalPhone(rawValue, countryCode || '+34');
    if (split) {
      const nextMax = getPhoneLengthRule(split.dial || '+34').max;
      onCountryCodeChange?.(split.dial);
      onChange(split.national.slice(0, nextMax));
      onAfterChange?.();
      return;
    }
    onChange(digitsOnly(rawValue).slice(0, maxNationalDigits));
    onAfterChange?.();
  };

  return (
    <div className="space-y-1.5 min-w-0">
      <div className="flex flex-wrap items-stretch gap-2 min-w-0">
        <div className="relative shrink-0" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={inputsLocked}
            className="flex h-11 min-w-0 items-center gap-1 px-2.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          >
            <span
              className={`fi fi-${getCountryIso2(selectedCountry)} rounded-sm shrink-0`}
              style={{ width: '1.1rem', height: '0.82rem' }}
            />
            <span className="text-xs font-medium text-slate-700">{selectedCountry.code}</span>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-slate-400 text-xs`} />
            </span>
          </button>
          {open && (
            <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-lg">
              <div className="p-2 border-b border-slate-100 bg-slate-50">
                <div className="relative">
                  <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                  <input
                    type="text"
                    className="w-full pl-8 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 bg-white"
                    placeholder="Search country or code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  )}
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {filteredCountries.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-400">No results found</div>
                ) : (
                  filteredCountries.map((c, idx) => (
                    <button
                      key={`${c.code}-${idx}`}
                      type="button"
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap ${
                        countryCode === c.code ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-700'
                      }`}
                      onClick={() => {
                        onCountryCodeChange?.(c.code);
                        setOpen(false);
                        setSearchTerm('');
                      }}
                    >
                      <span
                        className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`}
                        style={{ width: '1.1rem', height: '0.82rem' }}
                      />
                      <span className="font-medium w-10 shrink-0 text-left">{c.code}</span>
                      <span className="text-slate-500 truncate flex-1 text-left">{c.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <input
          className="min-h-[44px] min-w-0 flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition bg-white disabled:opacity-60 disabled:cursor-not-allowed"
          placeholder="6XX XXX XXX"
          inputMode="numeric"
          maxLength={maxNationalDigits}
          value={nationalValue}
          disabled={inputsLocked}
          onChange={(e) => applyRawPhoneValue(e.target.value)}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text');
            if (!pasted) return;
            e.preventDefault();
            applyRawPhoneValue(pasted);
          }}
        />
        <div className="inline-flex min-h-[44px] items-center gap-0.5 bg-slate-100 rounded-full p-0.5 shrink-0">
          <span className="text-[11px] font-semibold px-3 py-2 sm:py-1 rounded-full bg-white text-slate-800 shadow-sm whitespace-nowrap">
            SMS
          </span>
        </div>
        {verified ? (
          <span className="inline-flex min-h-[40px] items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded-lg whitespace-nowrap shrink-0">
            <i className="ri-checkbox-circle-fill text-emerald-500 text-xs shrink-0" aria-hidden />
            Verified
          </span>
        ) : !verifying ? (
          <button
            type="button"
            className="min-h-[44px] shrink-0 text-[11px] font-semibold text-slate-600 border border-slate-200 bg-white px-3 py-2 rounded-lg hover:border-slate-400 hover:text-slate-800 transition-colors cursor-pointer touch-manipulation whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={async () => {
              if (!String(value || '').trim() || busy) return;
              setOtpMessage('');
              if (!onRequestOtp) {
                setVerifying(true);
                return;
              }
              setBusy(true);
              try {
                const resp = await onRequestOtp();
                setVerifying(true);
                setOtpMessage(resp?.message || '');
              } catch (err) {
                const msg = err?.response?.data?.detail || err?.message || 'Failed to send code';
                const text = typeof msg === 'string' ? msg : 'Failed to send code';
                if (onError) onError(text);
                else showPopupMessage(text, 'error');
              } finally {
                setBusy(false);
              }
            }}
            disabled={!phoneValid || busy}
          >
            {busy ? 'Sending...' : 'Verify'}
          </button>
        ) : null}
      </div>
      {verifying && !verified && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap pl-0 sm:pl-1 min-w-0">
          <input
            className="w-full max-w-xs min-h-[44px] px-3 py-2 text-base font-mono border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 bg-white text-center tracking-[0.35em] sm:w-48 sm:tracking-[0.35em]"
            placeholder="------"
            value={code}
            maxLength={6}
            inputMode="numeric"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="min-h-[44px] text-[11px] font-semibold px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors cursor-pointer touch-manipulation whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={!/^\d{6}$/.test(code) || busy}
              onClick={async () => {
                if (/^\d{6}$/.test(code)) {
                  if (!onVerifyOtp) {
                    onVerifiedChange(true);
                    setLastVerifiedValue(value);
                    setVerifying(false);
                    setCode('');
                    return;
                  }
                  setBusy(true);
                  try {
                    await onVerifyOtp(code);
                    onVerifiedChange(true);
                    setLastVerifiedValue(value);
                    setVerifying(false);
                    setCode('');
                    setOtpMessage('');
                  } catch (err) {
                    const msg = err?.response?.data?.detail || err?.message || 'Failed to verify code';
                    const text = typeof msg === 'string' ? msg : 'Failed to verify code';
                    if (onError) onError(text);
                    else showPopupMessage(text, 'error');
                  } finally {
                    setBusy(false);
                  }
                }
              }}
            >
              {busy ? 'Confirming...' : 'Confirm'}
            </button>
            <button type="button" className="min-h-[44px] px-2 text-[11px] text-slate-400 hover:text-slate-600 cursor-pointer touch-manipulation whitespace-nowrap" onClick={() => { setVerifying(false); setCode(''); setOtpMessage(''); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

